import { RootState } from "../../redux/store";
import { UserRole } from "../global-types/global-types";

// TEMP: verbaiq-user-gate — remove once full RBAC ships
const ALL_ROLES: UserRole[] = [
  "vf-admin",
  "vf-editor",
  "vf-viewer",
  "superadmin",
  "admin",
  "editor",
  "commentor",
  "viewer",
];

export const isBTRClient = (industry?: string[]): boolean =>
  industry?.includes("btr") ?? false;

export const isSingleFamilyClient = (industry?: string[]): boolean =>
  industry?.includes("single_family") ?? false;

// BTR / HMO clients refer to their accommodation as apartments, so room-type labels
// surface as "Apartment Type" for them and "Room Type" for everyone else. Null-safe:
// a missing/empty industry array is treated as not-apartment.
export const isApartmentRoomTypeClient = (industry?: string[]): boolean =>
  industry?.some((i) => i === "btr" || i === "hmo") ?? false;

export const getAccommodationLabel = (industry?: string[]): string =>
  isBTRClient(industry)
    ? "Apartment Preferences"
    : isSingleFamilyClient(industry)
    ? "House Preferences"
    : "Accommodation Preferences";

export const getRoomTypeLabel = (industry?: string[]): string =>
  isBTRClient(industry)
    ? "Apartment Type"
    : isSingleFamilyClient(industry)
    ? "House Type"
    : "Room Type";

export const getClientFeatureConfig = (auth: RootState["auth"]) => {
  const verbaiqConfig = auth?.client?.verbaiq_config;
  // TEMP: verbaiq-user-gate — remove once full RBAC ships. Falls back to ALL_ROLES (not
  // ["vf-admin"]) so every role sees the nav item and builtin tiles; VerbaIQ-generated actions
  // (chat/chart/pin) are still gated server-side by vf-core's allowed_user_ids allowlist.
  //
  // VerbaIQ now splits into two surfaces (chat + analytics), each with its own enabled flag
  // and allowed_roles. During rollout a client may still send the legacy { enabled,
  // allowed_roles }, so each surface falls back to those, then to the defaults.
  const resolveVerbaiqRoles = (roles?: string[]) =>
    roles && roles.length
      ? roles
      : verbaiqConfig?.allowed_roles && verbaiqConfig.allowed_roles.length
      ? verbaiqConfig.allowed_roles
      : ALL_ROLES;

  const verbaiqChatEnabled =
    verbaiqConfig?.verbaiq_chat_enabled ?? verbaiqConfig?.enabled ?? true;
  const verbaiqAnalyticsEnabled =
    verbaiqConfig?.verbaiq_analytics_enabled ?? verbaiqConfig?.enabled ?? true;
  const verbaiqChatRoles = resolveVerbaiqRoles(
    verbaiqConfig?.verbaiq_chat_allowed_roles
  );
  const verbaiqAnalyticsRoles = resolveVerbaiqRoles(
    verbaiqConfig?.verbaiq_analytics_allowed_roles
  );
  const clientFeatureConfig = {
    // The legacy Analytics page keeps its original pre-VerbaIQ gate: the tenant feature lock only.
    // It deliberately carries NO allowed_roles — role enforcement for /dashboard/analytics lives in
    // featureRouteConfig.ts, as it did before VerbaIQ existed. An earlier revision (A6.1) borrowed
    // VerbaIQConfig.allowed_roles as a role ceiling here, which silently hid the restored legacy
    // page from every role outside that list (e.g. a tenant scoped to ["vf-admin"]) — the two
    // surfaces are independent, so VerbaIQ's role list must not gate this one.
    analytics_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.analytics_locked ?? false
      ),
    },
    dte_config: {
      enabled: true,
    },
    crm_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.crm_locked ?? false
      ),
    },
    pms_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.pms_locked ?? false
      ),
    },
    campaign_config: auth?.client?.campaign_config
      ? {
          enabled: !auth?.client?.campaign_config?.campaign_locked,
          allowed_roles: auth?.client?.campaign_config?.allowed_roles,
          subscribed_channels: auth?.client?.campaign_config
            ?.subscribed_channels ?? {
            call: false,
            email: false,
            whatsapp: false,
            sms: false,
          },
        }
      : {
          enabled: false,
          allowed_roles: [],
          subscribed_channels: {
            call: false,
            email: false,
            whatsapp: false,
            sms: false,
          },
        },
    playground_config: {
      enabled:
        auth?.client?.dashboard_config?.playground_config?.enabled ?? true,
      allowed_roles:
        auth?.client?.dashboard_config?.playground_config?.allowed_roles ?? [],
    },
    communication_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.communication_locked ??
        false
      ),
      subscribed_channels: {
        chat: !(
          auth?.client?.dashboard_config?.communication_lock?.chat_locked ??
          false
        ),
        call: !(
          auth?.client?.dashboard_config?.communication_lock?.call_locked ??
          false
        ),
        mail: !(
          auth?.client?.dashboard_config?.communication_lock?.mail_locked ??
          false
        ),
        whatsapp: !(
          auth?.client?.dashboard_config?.communication_lock?.whatsapp_locked ??
          false
        ),
        sms: !(
          auth?.client?.dashboard_config?.communication_lock?.sms_locked ??
          false
        ),
      },
      allowed_roles: [],
    },
    user_management_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.user_management_locked ??
        false
      ),
    },
    billing_config: {
      enabled:
        auth?.user?.role === "vf-admin" ||
        (auth?.user?.role === "superadmin" &&
          !(
            auth?.client?.dashboard_config?.feature_lock?.billing_locked ??
            false
          )),
    },
    settings_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.settings_locked ?? false
      ),
    },
    configurability_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.settings_locked ?? false
      ),
    },
    ticket_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.ticket_locked ?? false
      ),
    },
    alert_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.alert_locked ?? false
      ),
    },
    kam_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.kam_locked ?? false
      ),
    },
    user_manual_config: {
      enabled: !(
        auth?.client?.dashboard_config?.feature_lock?.user_manual_locked ??
        false
      ),
    },
    vf_copilot_config: {
      enabled: verbaiqChatEnabled,
      allowed_roles: verbaiqChatRoles,
    },
    verbaiq_analytics_config: {
      enabled: verbaiqAnalyticsEnabled,
      allowed_roles: verbaiqAnalyticsRoles,
    },
  };
  return clientFeatureConfig;
};
